import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  Locale, 
  Translations, 
  detectUserLocale, 
  getTranslations, 
  formatDate, 
  formatTime, 
  formatCurrency 
} from './index';

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  translations: Translations;
  t: (key: string) => string;
  formatDate: (date: Date) => string;
  formatTime: (date: Date) => string;
  formatCurrency: (amount: number) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
  defaultLocale?: Locale;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children, defaultLocale }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    // Check localStorage first, then use default or detect
    const savedLocale = localStorage.getItem('event-finder-locale') as Locale;
    return savedLocale || defaultLocale || detectUserLocale();
  });

  const [translations, setTranslations] = useState<Translations>(() => 
    getTranslations(locale)
  );

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('event-finder-locale', newLocale);
    setTranslations(getTranslations(newLocale));
  };

  // Translation function with nested key support
  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations;
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    return typeof value === 'string' ? value : key;
  };

  // Localized formatting functions
  const formatDateLocalized = (date: Date): string => formatDate(date, locale);
  const formatTimeLocalized = (date: Date): string => formatTime(date, locale);
  const formatCurrencyLocalized = (amount: number): string => formatCurrency(amount, locale);

  useEffect(() => {
    // Update translations when locale changes
    setTranslations(getTranslations(locale));
  }, [locale]);

  const value: I18nContextType = {
    locale,
    setLocale,
    translations,
    t,
    formatDate: formatDateLocalized,
    formatTime: formatTimeLocalized,
    formatCurrency: formatCurrencyLocalized,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

// Hook to use i18n context
export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

// Simple hook for just translations (lighter weight)
export const useTranslations = () => {
  const { t, translations } = useI18n();
  return { t, translations };
};

// Hook for locale information
export const useLocale = () => {
  const { locale, setLocale } = useI18n();
  return { locale, setLocale };
};
